import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { TextField, TextAreaField, SelectField } from '../components/ui/Field';
import { Skeleton } from '../components/ui/Skeleton';
import { IconArrowRight, IconWarn } from '../components/ui/icons';
import { Bloodline } from '../components/Bloodline';
import { assets, connect, listings } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { track } from '../lib/analytics';
import { useToast } from '../components/ui/Toast';
import type { AssetDetail, CreateListingResponse, LicenseType, ResolvedLineage } from '../lib/types';

const LICENSES: LicenseType[] = ['royalty_free', 'exclusive', 'sync', 'wellness'];

/**
 * Publish a pack — and, on publish, mint the bloodline (Build Plan 01 P2).
 *
 * The chain of custody has always existed in `asset_lineage`; until now it was
 * invisible at the only moment it means anything commercially. Publishing is when
 * the producer hands custody to a buyer, so publishing is where we show what the
 * buyer receives. The reveal is a consequence of the action, never a spectacle
 * bolted onto it (ROOMS §2).
 *
 * A draft is not a flip. Drafts get the quiet confirmation; only a published pack
 * mints.
 */
export function ListingComposer() {
  const { t, fmt } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const assetId = params.get('asset') ?? '';

  const [asset, setAsset] = useState<AssetDetail | null>(null);
  const [loadingAsset, setLoadingAsset] = useState(true);
  const [assetError, setAssetError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceDollars, setPriceDollars] = useState('');
  const [license, setLicense] = useState<LicenseType>('royalty_free');
  const [memberOnly, setMemberOnly] = useState(false);
  const [errors, setErrors] = useState<{ title?: string; price?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<CreateListingResponse | null>(null);
  /** Only a published pack flips; a draft hands custody to nobody. */
  const [published, setPublished] = useState(false);
  const [lineage, setLineage] = useState<ResolvedLineage | null>(null);

  /**
   * Publish-time payout gate: 'payable' | 'blocked' | 'unknown'. Drafts are never
   * blocked; publishing is gated only when we positively know payouts are not active
   * (a failed status read fails open so a Stripe hiccup can't brick publishing).
   */
  const [payoutGate, setPayoutGate] = useState<'unknown' | 'payable' | 'blocked'>('unknown');

  useEffect(() => {
    const controller = new AbortController();
    connect
      .status(controller.signal)
      .then((s) => setPayoutGate(s.state === 'active' ? 'payable' : 'blocked'))
      .catch(() => setPayoutGate('unknown'));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    let active = true;
    if (!assetId) {
      setLoadingAsset(false);
      setAssetError(t.errors.notFound);
      return;
    }
    assets
      .get(assetId)
      .then((a) => {
        if (!active) return;
        setAsset(a);
        setTitle((cur) => cur || a.title);
      })
      .catch((err) => active && setAssetError(apiErrorMessage(err, t)))
      .finally(() => active && setLoadingAsset(false));
    return () => {
      active = false;
    };
  }, [assetId, t]);

  // The listing is created in USD (`price_currency: 'USD'`, D-009/D-010), so the
  // field is dollars. It used to be labelled MXN and annotated with a ≈USD
  // conversion — a producer typing "50" was shown "≈ $3 USD" while the buyer was
  // charged $50.00.
  const priceCents = useMemo(() => {
    const dollars = Number(priceDollars);
    return Number.isFinite(dollars) && dollars > 0 ? Math.round(dollars * 100) : 0;
  }, [priceDollars]);

  function validate(): boolean {
    const next: typeof errors = {};
    if (title.trim().length === 0) next.title = t.errors.fieldRequired;
    if (priceCents <= 0) next.price = t.errors.fieldRequired;
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(e: FormEvent, publish: boolean) {
    e.preventDefault();
    if (publish && payoutGate === 'blocked') return; // gate publishes, never drafts
    if (!validate() || !assetId) return;
    setSubmitting(true);
    try {
      const res = await listings.create({
        asset_id: assetId,
        title: title.trim(),
        description: description.trim() || undefined,
        price_cents: priceCents,
        price_currency: 'USD',
        license_type: license,
        member_only: memberOnly,
        publish,
      });
      setCreated(res);
      setPublished(publish);
      toast.push(publish ? t.listing.publishedTitle : t.listing.draftCta, 'success');

      if (publish) {
        // Funnel step: a pack reached the market (drafts don't count).
        track('pack_listed', { listing_id: res.id, price_cents: priceCents, license });
        // Mint the bloodline. A lineage read that fails must never make a
        // successful publish look failed — the reveal degrades to the plain
        // confirmation, and the chain stays available on the listing itself.
        assets
          .lineage(assetId)
          .then((res2) => setLineage(res2.lineage))
          .catch(() => setLineage(null));
      }
    } catch (err) {
      toast.push(apiErrorMessage(err, t), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  if (created) {
    return (
      <div>
        <PageHeader title={t.listing.title} />
        <Card className="border-border-strong p-6 sm:p-8">
          <h2 className="type-display-lg text-primary">
            {published ? t.listing.publishedTitle : t.listing.draftSavedTitle}
          </h2>
          <p className="type-body text-secondary mt-2 max-w-prose">
            {published ? t.listing.publishedBody : t.listing.draftSavedBody}
          </p>

          {/* The flip: what the buyer receives with the pack. */}
          {published && lineage ? (
            <div className="border-border-subtle mt-8 border-t pt-6">
              <Bloodline lineage={lineage} />
            </div>
          ) : null}

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/market/${created.id}`}>
              <Button size="lg" iconRight={<IconArrowRight size={18} />}>
                {t.listing.viewInMarket}
              </Button>
            </Link>
            <Button variant="ghost" size="lg" onClick={() => navigate('/bench')}>
              {t.bench.title}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t.listing.title} subtitle={t.listing.subtitle} />

      {/* Publish-time payout gate (drafts stay open) */}
      {payoutGate === 'blocked' ? (
        <div className="mb-6 flex max-w-xl items-start gap-3 rounded border border-warn bg-warn-tint p-4" role="status">
          <span className="text-warn mt-0.5 shrink-0">
            <IconWarn size={18} />
          </span>
          <div>
            <p className="type-body-sm text-primary">{t.payouts.composerNotice}</p>
            <Link to="/account" className="type-label text-warn hover:text-primary focus-ring mt-2 inline-flex rounded-sm">
              {t.payouts.composerCta} →
            </Link>
          </div>
        </div>
      ) : null}

      {/* Source asset summary */}
      <Card className="mb-6 p-4">
        <p className="type-label text-secondary mb-2">{t.listing.fromAsset}</p>
        {loadingAsset ? (
          <Skeleton className="h-6 w-1/2" />
        ) : assetError ? (
          <p className="type-body-sm text-danger">{assetError}</p>
        ) : asset ? (
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="type-heading-2 text-primary">{asset.title}</span>
            <span className="type-data text-secondary tnum">
              {fmt.duration(asset.duration_ms)} · {fmt.bpm(asset.bpm) || '—'} · {asset.kind.replace('_', ' ').toUpperCase()}
            </span>
          </div>
        ) : null}
      </Card>

      <form className="flex max-w-xl flex-col gap-5" onSubmit={(e) => onSubmit(e, true)} noValidate>
        <TextField
          label={t.listing.titleLabel}
          placeholder={t.listing.titlePlaceholder}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={errors.title}
          maxLength={200}
          required
        />
        <TextAreaField
          label={t.listing.descLabel}
          placeholder={t.listing.descPlaceholder}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          maxLength={4000}
        />

        {/* Dollars, in USD, priced exactly as the buyer sees it (D-009/D-010). */}
        <TextField
          label={t.listing.priceLabel}
          type="number"
          inputMode="decimal"
          min={1}
          step="0.01"
          suffix="USD"
          value={priceDollars}
          onChange={(e) => setPriceDollars(e.target.value)}
          error={errors.price}
          required
        />

        <SelectField label={t.listing.licenseLabel} value={license} onChange={(e) => setLicense(e.target.value as LicenseType)}>
          {LICENSES.map((l) => (
            <option key={l} value={l}>
              {t.listing.license[l]}
            </option>
          ))}
        </SelectField>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={memberOnly}
            onChange={(e) => setMemberOnly(e.target.checked)}
            className="h-5 w-5 accent-accent focus-ring rounded-sm"
          />
          <span className="type-body-sm text-secondary">{t.listing.memberOnly}</span>
        </label>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button type="submit" size="lg" loading={submitting} disabled={!asset || payoutGate === 'blocked'}>
            {submitting ? t.listing.publishing : t.listing.publishCta}
          </Button>
          <Button type="button" variant="ghost" size="lg" onClick={(e) => onSubmit(e, false)} disabled={submitting || !asset}>
            {t.listing.draftCta}
          </Button>
        </div>
      </form>
    </div>
  );
}
